import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import { UseRpiPaginationResult } from "../hooks/usePagination";

const usePaginationStyles = createStyles({
  wrapper: {
    display: "flex",
    flex: "auto",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `12px ${tokens.spacing[4]}`,

    color: tokens.color.semantic.text.subtle,
    fontSize: tokens.font.size.sm,
    "font-weight": tokens.font.weight.normal,
    lineHeight: tokens.font.lineHeight.normal,
  },
  carousel: {
    display: "flex",
    flexDirection: "row",
    gap: tokens.spacing[2],
    alignItems: "center",
  },
  ellipsis: {
    width: "32px",
    height: "32px",
    fontSize: tokens.font.size.base,
    textAlign: "center",
    alignContent: "end",
  },
  button: {
    padding: 0,
    textDecoration: "none",
  },
  pageButton: {
    width: "32px",
    height: "32px",
    "&[data-current-page=true]": {
      color: tokens.color.semantic.text.primary,
      backgroundColor: tokens.color.semantic.bg.primarySubtlest,
      borderRadius: tokens.radius.sm,
    },
  },
  navButton: {
    color: tokens.color.semantic.icon.bold,
    "&:disabled": {
      color: tokens.color.semantic.icon.disabled,
    },
  },
});

interface PaginationProps {
  pagination: UseRpiPaginationResult;
  isLoading?: boolean;
  /** Noun displayed in "Showing X-Y of Z {label}" (default: "Products") */
  label?: string;
}

/**
 * Build the list of page indices (0-based) and ellipsis markers to render.
 *
 * Strategy:
 *  - Always show first and last page.
 *  - Near start (page <= 3): show first 5 pages, then ellipsis, then last.
 *  - Near end (page >= pages - 4): show first, then ellipsis, then last 5.
 *  - Middle: show first, ellipsis, prev, current, next, ellipsis, last.
 *  - When total pages <= 7, show every page (no ellipsis needed).
 */
function buildPageItems(page: number, pages: number): Array<number | "ellipsis"> {
  if (pages <= 7) {
    return Array.from({ length: pages }, (_, i) => i);
  }

  const items = new Set<number>();
  items.add(0);
  items.add(pages - 1);

  if (page <= 3) {
    // Near start: show first 5
    for (let i = 0; i < 5; i++) items.add(i);
  } else if (page >= pages - 4) {
    // Near end: show last 5
    for (let i = pages - 5; i < pages; i++) items.add(i);
  } else {
    // Middle: current + neighbors
    items.add(page - 1);
    items.add(page);
    items.add(page + 1);
  }

  const sorted = [...items].sort((a, b) => a - b);

  const result: Array<number | "ellipsis"> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]);
  }
  return result;
}

export function Pagination({ pagination, isLoading, label = "Products" }: PaginationProps) {
  const { classes, cx } = usePaginationStyles();
  const pageNumberButton = cx(classes.button, classes.pageButton);
  const navButton = cx(classes.button, classes.navButton);
  if (pagination.pages === 0) {
    return null;
  }

  const pageItems = buildPageItems(pagination.page, pagination.pages);

  return (
    <div className={classes.wrapper}>
      {`Showing ${pagination.page * pagination.perPage + 1}-${Math.min(
        pagination.page * pagination.perPage + pagination.perPage,
        pagination.totalCount
      )} of ${pagination.totalCount} ${label}`}

      <div className={classes.carousel}>
        <Button
          key="paginate-back"
          variant="text"
          intent="neutral"
          className={navButton}
          onClick={pagination.previousPage}
          disabled={isLoading || pagination.page === 0}
        >
          <IconV3 icon="chevron_left" color="inherit" size={24} />
        </Button>
        {pageItems.map((item, i) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${i}`} className={classes.ellipsis}>
              ...
            </span>
          ) : (
            <Button
              key={item}
              className={pageNumberButton}
              variant="text"
              intent="neutral"
              onClick={() => pagination.setPage(item)}
              disabled={isLoading || pagination.page === item}
              data-current-page={pagination.page === item}
            >
              {item + 1}
            </Button>
          )
        )}
        <Button
          key="paginate-forward"
          variant="text"
          intent="neutral"
          className={navButton}
          onClick={pagination.nextPage}
          disabled={isLoading || pagination.page === pagination.pages - 1}
        >
          <IconV3 icon="chevron_right" color="inherit" size={24} />
        </Button>
      </div>
    </div>
  );
}
